import { memo } from 'react';
import type { TacticsDrawing } from '@4cm/core/tactics/drawings';
import { measureDistanceMetres } from '@4cm/core/tactics/drawings';
import { useRegion } from '../../lib/i18n/useRegion';
import { hexWithAlpha, formatMeasureLabel } from '../tactics3d/drawingHelpers';

/**
 * §7 Drawing layer for the 2D tactics board. Renders one SVG element
 * per `TacticsDrawing` entry — line / arrow / curved-arrow / rect /
 * ellipse / text / freehand.
 *
 * Coordinate mapping: the parent `PitchSvg` viewBox is in world metres
 * with the landscape axis swap `SVG-x = -world.z`, `SVG-y = world.x`
 * (see PitchSvg.tsx + MovementArrow2D.tsx). Every drawing's
 * `{x, z}` points are therefore emitted with that swap so a drawing
 * authored against world coords renders at the same pitch position the
 * 3D board would.
 *
 * Arrow heads use scoped `<marker>` defs (id prefix `drawing-ah-`) so
 * they don't collide with the global `t2d-ah` marker that MovementArrow2D
 * binds to. Each marker is coloured via `fill="currentColor"` and the
 * parent `<g>` sets `color: drawing.stroke` so the head matches the
 * shaft hex without per-marker fill overrides.
 */

export interface DrawingLayer2DProps {
  drawings: TacticsDrawing[];
}

// World → SVG point swap — pure helper so freehand and curved-arrow
// builders share one formula.
function svgX(p: { x: number; z: number }): number {
  return -p.z;
}
function svgY(p: { x: number; z: number }): number {
  return p.x;
}

// `hexWithAlpha` is shared with DrawingLayer3D — see drawingHelpers.ts.

// Translate the abstract dash style into SVG stroke-dasharray. Values
// are in world metres because the viewBox is metres — picking these
// against the same scale as the pitch markings (0.2-0.25m strokes) keeps
// the patterns visible without dominating tokens.
function dashArrayFor(
  dash: TacticsDrawing['dash'],
  strokeWidth: number,
): string | undefined {
  if (!dash || dash === 'solid') return undefined;
  if (dash === 'dashed') {
    return `${strokeWidth * 4} ${strokeWidth * 2.5}`;
  }
  // dotted — short on/off cycle so it reads as discrete dots
  return `${strokeWidth * 0.6} ${strokeWidth * 1.8}`;
}

interface ArrowMarkerProps {
  id: string;
  head: NonNullable<TacticsDrawing['arrowHead']>;
}

/** One SVG `<marker>` element matching the requested head style.
 *  `currentColor` lets the parent `<g style={{ color }}>` drive the
 *  fill / stroke without per-marker prop wiring. */
function ArrowMarker({ id, head }: ArrowMarkerProps) {
  if (head === 'none') return null;
  if (head === 'open') {
    // Outline-only V shape — stroke not fill.
    return (
      <marker
        id={id}
        viewBox="0 0 10 10"
        refX="8"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M0,0 L10,5 L0,10" fill="none" stroke="currentColor" strokeWidth={1.5} />
      </marker>
    );
  }
  if (head === 'curved') {
    // Subtle curved chevron — same outline-only treatment as 'open' but
    // bowed inward so the head reads as a softened curve at the tip.
    return (
      <marker
        id={id}
        viewBox="0 0 10 10"
        refX="8"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path
          d="M0,0 Q6,5 0,10"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
        />
      </marker>
    );
  }
  // standard — filled triangle, mirrors the global t2d-ah marker so the
  // visual language stays consistent with MovementArrow2D.
  return (
    <marker
      id={id}
      viewBox="0 0 10 10"
      refX="8"
      refY="5"
      markerWidth="6"
      markerHeight="6"
      orient="auto-start-reverse"
    >
      <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
    </marker>
  );
}

// `formatMeasureLabel` is shared with DrawingLayer3D — see drawingHelpers.ts.

function DrawingShape({
  drawing,
  distanceUnit,
}: {
  drawing: TacticsDrawing;
  distanceUnit: 'metres' | 'yards';
}) {
  const dashArray = dashArrayFor(drawing.dash, drawing.strokeWidth);
  const markerId = `drawing-ah-${drawing.id}`;
  const head = drawing.arrowHead ?? 'none';
  const needsMarker =
    (drawing.kind === 'arrow' || drawing.kind === 'curved-arrow') && head !== 'none';

  // -------- Lines / arrows / curved arrows --------
  if (drawing.kind === 'line' || drawing.kind === 'arrow') {
    if (drawing.points.length < 2) return null;
    const a = drawing.points[0];
    const b = drawing.points[drawing.points.length - 1];
    return (
      <g style={{ color: drawing.stroke }} data-kind={drawing.kind}>
        {needsMarker && (
          <defs>
            <ArrowMarker id={markerId} head={head} />
          </defs>
        )}
        <line
          x1={svgX(a)}
          y1={svgY(a)}
          x2={svgX(b)}
          y2={svgY(b)}
          stroke={drawing.stroke}
          strokeWidth={drawing.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={dashArray}
          fill="none"
          markerEnd={needsMarker ? `url(#${markerId})` : undefined}
        />
      </g>
    );
  }

  if (drawing.kind === 'curved-arrow') {
    if (drawing.points.length < 2) return null;
    const a = drawing.points[0];
    const b = drawing.points[drawing.points.length - 1];
    // One bezier control point — explicit if a 3-point path was captured,
    // otherwise infer a midpoint bowed perpendicular to the straight line
    // so a 2-point input still curves.
    const mid =
      drawing.points.length >= 3
        ? drawing.points[1]
        : (() => {
            const cx = (a.x + b.x) / 2;
            const cz = (a.z + b.z) / 2;
            // Perpendicular offset in world metres — 15% of segment length.
            const dx = b.x - a.x;
            const dz = b.z - a.z;
            const len = Math.sqrt(dx * dx + dz * dz);
            const k = len === 0 ? 0 : 0.15 * len;
            return { x: cx - (dz / Math.max(len, 1e-6)) * k, z: cz + (dx / Math.max(len, 1e-6)) * k };
          })();
    const d = `M${svgX(a)},${svgY(a)} Q${svgX(mid)},${svgY(mid)} ${svgX(b)},${svgY(b)}`;
    return (
      <g style={{ color: drawing.stroke }} data-kind="curved-arrow">
        {needsMarker && (
          <defs>
            <ArrowMarker id={markerId} head={head} />
          </defs>
        )}
        <path
          d={d}
          fill="none"
          stroke={drawing.stroke}
          strokeWidth={drawing.strokeWidth}
          strokeLinecap="round"
          strokeDasharray={dashArray}
          markerEnd={needsMarker ? `url(#${markerId})` : undefined}
        />
      </g>
    );
  }

  // -------- Rect --------
  if (drawing.kind === 'rect') {
    if (drawing.points.length < 2) return null;
    const a = drawing.points[0];
    const b = drawing.points[1];
    const ax = svgX(a);
    const ay = svgY(a);
    const bx = svgX(b);
    const by = svgY(b);
    const x = Math.min(ax, bx);
    const y = Math.min(ay, by);
    const w = Math.abs(bx - ax);
    const h = Math.abs(by - ay);
    return (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill={drawing.fill ?? 'transparent'}
        stroke={drawing.stroke}
        strokeWidth={drawing.strokeWidth}
        strokeDasharray={dashArray}
        data-kind="rect"
      />
    );
  }

  // -------- Ellipse --------
  if (drawing.kind === 'ellipse') {
    if (drawing.points.length < 2) return null;
    const a = drawing.points[0];
    const b = drawing.points[1];
    const ax = svgX(a);
    const ay = svgY(a);
    const bx = svgX(b);
    const by = svgY(b);
    const cx = (ax + bx) / 2;
    const cy = (ay + by) / 2;
    const rx = Math.abs(bx - ax) / 2;
    const ry = Math.abs(by - ay) / 2;
    return (
      <ellipse
        cx={cx}
        cy={cy}
        rx={rx}
        ry={ry}
        fill={drawing.fill ?? 'transparent'}
        stroke={drawing.stroke}
        strokeWidth={drawing.strokeWidth}
        strokeDasharray={dashArray}
        data-kind="ellipse"
      />
    );
  }

  // -------- Text --------
  if (drawing.kind === 'text') {
    if (drawing.points.length < 1) return null;
    const a = drawing.points[0];
    return (
      <text
        x={svgX(a)}
        y={svgY(a)}
        fill={drawing.stroke}
        fontSize={2.5}
        fontWeight={600}
        dominantBaseline="middle"
        textAnchor="middle"
        style={{ userSelect: 'none', letterSpacing: 0.1 }}
        data-kind="text"
      >
        {drawing.text ?? ''}
      </text>
    );
  }

  // -------- Distance measure --------
  // Piero / Sky-telestrator "distance between players" — a dashed line
  // segment plus a printed length label (metres for UK, yards for US).
  // Two-point input; the label is anchored just above the midpoint so
  // it reads clearly on the dark stadium pitch without obscuring the
  // segment itself.
  if (drawing.kind === 'measure') {
    if (drawing.points.length < 2) return null;
    const a = drawing.points[0];
    const b = drawing.points[drawing.points.length - 1];
    const distM = measureDistanceMetres(a, b);
    const labelText = formatMeasureLabel(distM, distanceUnit);
    // Midpoint in SVG-space — text is anchored slightly above the line
    // (textAnchor middle + a small y offset opposite the segment so the
    // label hugs the wire without overlapping it).
    const ax = svgX(a);
    const ay = svgY(a);
    const bx = svgX(b);
    const by = svgY(b);
    const mx = (ax + bx) / 2;
    const my = (ay + by) / 2;
    // Offset perpendicular to the segment so the label clears the line.
    // ~1.6 world-metres feels right against the standard pitch markings.
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.sqrt(dx * dx + dy * dy);
    const LABEL_OFFSET_M = 1.6;
    const nx = len === 0 ? 0 : -dy / len;
    const ny = len === 0 ? -1 : dx / len;
    const tx = mx + nx * LABEL_OFFSET_M;
    const ty = my + ny * LABEL_OFFSET_M;
    return (
      <g data-kind="measure">
        <line
          x1={ax}
          y1={ay}
          x2={bx}
          y2={by}
          stroke={drawing.stroke}
          strokeWidth={drawing.strokeWidth}
          strokeLinecap="round"
          // Always dashed for the measure tool — communicates "this is
          // a reference annotation, not a movement arrow".
          strokeDasharray={dashArray ?? `${drawing.strokeWidth * 4} ${drawing.strokeWidth * 2.5}`}
          fill="none"
        />
        <text
          x={tx}
          y={ty}
          fill={drawing.stroke}
          fontSize={2.2}
          fontWeight={600}
          dominantBaseline="middle"
          textAnchor="middle"
          style={{ userSelect: 'none', letterSpacing: 0.05 }}
          data-testid="t2d-measure-label"
        >
          {labelText}
        </text>
      </g>
    );
  }

  // -------- Manual spacing zone (polygon) --------
  // Tap-corner-by-corner polygon authored via the zone tool. Closed
  // by repeating the first point; rendered with a thin outline and a
  // semi-transparent fill that picks up the stroke colour so the
  // polygon reads as a tinted region matching the coach's palette pick.
  if (drawing.kind === 'zone') {
    if (drawing.points.length < 3) return null;
    const pts = drawing.points.map((p) => `${svgX(p)},${svgY(p)}`).join(' ');
    // Derive a semi-transparent fill from the stroke hex unless the
    // caller passed an explicit `fill`. 'auto' is the factory sentinel
    // that means "follow the stroke colour".
    const fill =
      drawing.fill && drawing.fill !== 'auto' && drawing.fill !== 'transparent'
        ? drawing.fill
        : hexWithAlpha(drawing.stroke, 0.22);
    return (
      <polygon
        points={pts}
        fill={fill}
        stroke={drawing.stroke}
        strokeWidth={drawing.strokeWidth}
        strokeLinejoin="round"
        strokeDasharray={dashArray}
        data-kind="zone"
      />
    );
  }

  // -------- Freehand polyline --------
  if (drawing.kind === 'freehand') {
    if (drawing.points.length < 2) return null;
    const pts = drawing.points.map((p) => `${svgX(p)},${svgY(p)}`).join(' ');
    return (
      <polyline
        points={pts}
        fill="none"
        stroke={drawing.stroke}
        strokeWidth={drawing.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={dashArray}
        data-kind="freehand"
      />
    );
  }

  return null;
}

function DrawingLayer2DInner({ drawings }: DrawingLayer2DProps) {
  // Region-aware distance unit for the measure tool. Read at the layer
  // root so every drawing renders against the same active pack — calling
  // `useRegion()` inside `DrawingShape` would re-subscribe per shape.
  const region = useRegion();
  const distanceUnit = region.units.distance;
  if (!drawings || drawings.length === 0) return null;
  return (
    <g className="t2d-drawing-layer" data-testid="t2d-drawing-layer">
      {drawings.map((d) => (
        <g key={d.id} data-drawing-id={d.id} data-testid={`t2d-drawing-${d.id}`}>
          <DrawingShape drawing={d} distanceUnit={distanceUnit} />
        </g>
      ))}
    </g>
  );
}

/** Memoised — drawings array is stable across token drags so we skip
 *  the whole subtree when only player positions change. Mirrors the
 *  ZoneLayer2D pattern. */
export const DrawingLayer2D = memo(DrawingLayer2DInner);
